import { useEffect, useRef, useState } from "react";

import { computeLoadingPhase, type LoadingPhase } from "@/lib/loading-phase";

/**
 * The React wiring around `computeLoadingPhase`'s decision (same-named
 * module, `lib/loading-phase.ts`) — a real timer, nothing else. Kept
 * deliberately thin: every rule this hook needs to get right lives in the
 * pure function, which is what `loading-phase.test.ts` exercises directly.
 * Live proof that this hook is wired to a real screen correctly is
 * VC-2.2-15's throttled-network browser check, not a unit test of the hook
 * itself — this codebase has no DOM test environment (Vitest's default
 * `node` environment; no `jsdom`/`@testing-library/react` dependency exists
 * to add one without violating the story's "no new pinned dependency" rule,
 * see the discharge report) to mount a live, re-rendering hook against.
 */
export function useLoadingPhase(isPending: boolean): LoadingPhase {
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isPending) {
      startedAtRef.current = null;
      setElapsedMs(0);
      return;
    }
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    const intervalId = window.setInterval(() => {
      const startedAt = startedAtRef.current;
      if (startedAt !== null) {
        setElapsedMs(Date.now() - startedAt);
      }
    }, 100);
    return () => window.clearInterval(intervalId);
  }, [isPending]);

  return computeLoadingPhase(isPending, elapsedMs);
}
